import React, { useEffect, useRef, useState } from 'react';

import cx from 'classnames';
import { map } from 'lodash';

import Icon from '../icons';

import styles from './TabLayout.module.scss';

interface Props {
  direction?: 'row' | 'column';
  lineStyle?: 'long' | 'short';
  tabs: { label?: string; icon?: React.JSX.Element }[];
  tabsContent?: React.ReactNode[];
  width?: string;
  contanierClassName?: string;
  tabClassName?: string;
  tabHeaderClassName?: string;
}

const TabLayout: React.FC<Props> = (props: Props) => {
  const {
    direction,
    lineStyle,
    tabs,
    tabsContent,
    width,
    tabClassName,
    contanierClassName,
    tabHeaderClassName,
  } = props;

  const [activeTab, setActiveTab] = React.useState<number>(0);
  const [tabWidth, setTabWidth] = useState<number[]>([]);
  const tabWidthRef = useRef<number[]>([]);

  const tabRefs = useRef<Array<React.RefObject<HTMLDivElement>>>([]);

  const labelwidthref = useRef<number[]>([]);

  const sliderRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const slider = sliderRef.current;
    let isDown = false;
    let startX: number = 0;
    let scrollLeft: number = 0;

    const onMouseDown = (e: MouseEvent) => {
      isDown = true;

      if (slider) {
        slider.classList.add('active');
        startX = e.pageX - slider.offsetLeft;
        scrollLeft = slider.scrollLeft;
      }
    };

    const onMouseLeave = () => {
      isDown = false;
      slider?.classList.remove('active');
    };

    const onMouseUp = () => {
      isDown = false;
      slider?.classList.remove('active');
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDown) return;
      e.preventDefault();
      if (slider) {
        const x = e.pageX - slider.offsetLeft;
        const walk = (x - startX) * 3; //scroll-fast
        slider.scrollLeft = scrollLeft - walk;
      }
    };

    if (slider) {
      slider.addEventListener('mousedown', onMouseDown);
      slider.addEventListener('mouseleave', onMouseLeave);
      slider.addEventListener('mouseup', onMouseUp);
      slider.addEventListener('mousemove', onMouseMove);

      return () => {
        slider.removeEventListener('mousedown', onMouseDown);
        slider.removeEventListener('mouseleave', onMouseLeave);
        slider.removeEventListener('mouseup', onMouseUp);
        slider.removeEventListener('mousemove', onMouseMove);
      };
    }
  }, []);

  useEffect(() => {
    const slider = sliderRef.current;
    if (slider) {
      const tabWidth = map(
        Array.from(slider.children),
        (child) => child.clientWidth
      );
      setTabWidth(tabWidth);
      tabWidthRef.current = tabWidth;
    }
  }, []);

  // make array of ref

  useEffect(() => {
    tabRefs.current = tabs.map(() => React.createRef());

    labelwidthref.current = tabRefs.current.map((ref) => {
      if (ref.current) {
        return ref.current.children[1].clientWidth;
      }
      return 0;
    });
  }, [tabs]);

  //* find the width of the label in the tab

  const handleTabClick = (tab: number) => {
    setActiveTab(tab);
  };

  return (
    <div
      className={cx(styles.tabBar, contanierClassName)}
      style={{
        width: width,
      }}>
      <div
        className={cx(styles.settingsHeader, tabHeaderClassName)}
        ref={sliderRef}>
        {map(tabs, (tab, index) => (
          <div
            className={cx(
              styles.tab,
              tabClassName,
              { [styles.activeTab]: activeTab === index },

              { [styles.directionRow]: direction === 'row' },
              { [styles.directionColumn]: direction === 'column' },
              {
                [styles.lineStyleLong]:
                  activeTab === index && lineStyle === 'long',
              },
              {
                [styles.lineStyleShort]:
                  activeTab === index && lineStyle === 'short',
              }
            )}
            onClick={() => handleTabClick(index)}
            key={`tab-${index}`}
            ref={tabRefs.current[index]}>
            <Icon icon={tab.icon} />
            <p
              className={styles.tabLabel}
              style={{
                marginLeft: tab.icon ? '8px' : '0px',
              }}>
              {tab.label}
            </p>
          </div>
        ))}

        <span
          className={cx(styles.line)}
          style={{
            width: tabWidth[activeTab],
            transform: `translateX(${tabWidthRef.current
              .slice(0, activeTab)
              .reduce((acc, cur) => acc + cur, 0)}px)`,
          }}></span>
      </div>

      <div className={styles.child}>
        {tabsContent && tabsContent[activeTab]}
      </div>
    </div>
  );
};

export default TabLayout;
